<template>
  <div
    class="card rs-collapse-lite rs-custom-card"
  >
    <div
      class="card-header"
      :class="`p-0 ${headerClass}`"
    >
      <div
        class="row m-0 align-items-center "
      >
        <div
          v-show="hasDrag"
          class="col flex-grow-0 cursor-grabbing "
        >
          <i class="fas fa-grip-vertical rs-text-secondary-light" />
        </div>

        <div
          :class="`col p-0 ${noDragClass} `"
        >
          <button
            :id="`${id}_toggle`"
            type="button"
            class="btn btn-block"
            :class="`${headerClass}`"
            @click="toggle"
          >
            <slot name="title" />
            <div
              v-if="theLabel"
              :class="`row m-0 align-items-center` "
            >
              <div
                v-show="hasSwitch"
                class="col p-0 flex-grow-0 mr-3"
              >
                <RsFormSwitch
                  :id="`${id}_switch`"
                  :model-value="boundValue"
                  :true-value="true"
                  :false-value="false"
                  class="mb-0"
                  @change="toggle"
                />
              </div>
              <div
                v-show="icon"
                class="col p-0 flex-grow-0 mr-3"
              >
                <i :class="icon" />
              </div>

              <div
                class="col p-0 text-truncate"
              >
                {{ theLabel }}
                <i
                  v-show="listingScoresHeart"
                  :id="`${id}_lsCollapse`"
                  class="far fa-heartbeat ml-1"
                >
                  <RsTooltip
                    v-if="listingScoresHeart"
                    :target="`${id}_lsCollapse`"
                    position="right"
                    :title="listingScoresText"
                  />
                </i>
              </div>

              <div class="`col p-0 ml-3 flex-grow-0">
                <i
                  v-show="boundValue"
                  :class="toggleUpIcon"
                />
                <i
                  v-show="!boundValue"
                  :class="toggleDownIcon"
                />
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="renderType === 'if' ? boundValue : true"
      :id="`${id}`"
      class="collapse"
      :class="{ show: boundValue }"
    >
      <div :class="`card-body border-top ${noDragClass} ${bodyClass}`">
        <slot />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import { decodeHtml } from '@/shared';

export default defineComponent({
  name: 'RsCollapseLite',

  props: {
    id: {
      type: String,
      required: true,
    },

    label: {
      type: String,
    },

    icon: {
      type: String,
      default: '',
    },

    value: {
      type: Boolean,
      default: false,
    },

    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so both
     * `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input` consumers
     * work. `undefined` means the parent did not bind v-model (see `boundValue`).
     */
    modelValue: {
      type: Boolean,
      default: undefined,
    },

    hasDrag: {
      type: Boolean,
      default: false,
    },

    listingScoresHeart: {
      type: Boolean,
      default: false,
    },

    listingScoresText: {
      type: String,
      default: '',
    },

    hasSwitch: {
      type: Boolean,
      default: false,
    },

    noDragClass: {
      type: String,
      default: '',
    },

    bodyClass: {
      type: String,
      default: '',
    },

    headerClass: {
      type: String,
      default: '',
    },

    toggleDownIcon: {
      type: String,
      default: 'far fa-chevron-down',
    },

    toggleUpIcon: {
      type: String,
      default: 'far fa-chevron-up',
    },

    renderType: {
      type: String,
      validator: (prop?: 'if' | 'show') => ['if', 'show'].includes(prop ?? 'if'),
      default: 'if',
    },
  },

  emits: ['input', 'update:modelValue'],

  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): boolean {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },

    theLabel(): string {
      return decodeHtml(this.label || '').replace(/<\/?[^>]+(>|$)/g, '');
    },
  },

  methods: {
    toggle(): void {
      const out = !this.boundValue;
      this.$emit('input', out);
      this.$emit('update:modelValue', out);
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/styles/_variables';

.fa-heartbeat{
  color: $error;
}
.active-header{
  background: $tertiary-gray !important;
  border-radius: 6px 6px 0 0;
}

</style>
